import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { getDB } from '../config/firebase';
import { ClassService } from './classService';
import { Class, Student } from '../types';

export interface AttendanceData {
  id: string;
  studentId: string;
  studentName: string;
  rollNumber: string;
  classId: string;
  className: string;
  date: Date;
  status: 'Present' | 'Absent';
}

export interface ReportFilters {
  reportType: 'attendance' | 'student' | 'class' | 'summary';
  dateRange: 'today' | 'week' | 'month' | 'semester' | 'custom';
  startDate?: Date;
  endDate?: Date;
  classId?: string;
  studentId?: string;
}

export interface AttendanceStats {
  totalSessions: number;
  presentSessions: number;
  absentSessions: number;
  attendanceRate: number;
}

export interface ClassReport {
  classId: string;
  className: string;
  totalStudents: number;
  attendanceStats: AttendanceStats;
  students: StudentAttendanceReport[];
}

export interface StudentAttendanceReport {
  studentId: string;
  studentName: string;
  rollNumber: string;
  attendanceStats: AttendanceStats;
  recentAttendance: AttendanceData[];
}

export interface ReportSummary {
  totalClasses: number;
  totalStudents: number;
  overallAttendanceRate: number;
  classReports: ClassReport[];
  topPerformingClasses: { className: string; attendanceRate: number }[];
  lowAttendanceStudents: { studentName: string; className: string; attendanceRate: number }[];
}

export class ReportService {
  // Get date range based on filter
  static getDateRange(dateRange: string, startDate?: Date, endDate?: Date): { start: Date; end: Date } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);
        return { start: weekStart, end: weekEnd };
      
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return { start: monthStart, end: monthEnd };
      
      case 'semester':
        // Assuming semester starts in January or July
        const currentMonth = today.getMonth();
        const semesterStart = currentMonth < 6 
          ? new Date(today.getFullYear(), 0, 1) 
          : new Date(today.getFullYear(), 6, 1);
        const semesterEnd = currentMonth < 6 
          ? new Date(today.getFullYear(), 6, 1) 
          : new Date(today.getFullYear() + 1, 0, 1);
        return { start: semesterStart, end: semesterEnd };
      
      case 'custom':
        return { 
          start: startDate || today, 
          end: endDate || new Date(today.getTime() + 24 * 60 * 60 * 1000) 
        };
      
      default:
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    }
  }

  // Extract attendance data from class documents (correct Firebase structure)
  static async getAttendanceData(filters: ReportFilters): Promise<AttendanceData[]> {
    try {
      const { start, end } = this.getDateRange(filters.dateRange, filters.startDate, filters.endDate);
      const attendanceData: AttendanceData[] = [];
      
      console.log('Fetching attendance data for date range:', start.toDateString(), 'to', end.toDateString());
      
      // Get classes directly from Firebase
      const classesSnapshot = await getDocs(collection(getDB(), 'classes'));
      
      for (const classDoc of classesSnapshot.docs) {
        const classData = classDoc.data();
        
        // Skip if filtering by specific class and this isn't it
        if (filters.classId && classDoc.id !== filters.classId) {
          continue;
        }
        
        console.log(`Processing class: ${classData.name || classDoc.id} with ${classData.students?.length || 0} students`);
        
        // Extract attendance data from students array in class document
        if (classData.students && Array.isArray(classData.students)) {
          classData.students.forEach((student: any) => {
            // Filter by student if specified
            if (filters.studentId && student.id?.toString() !== filters.studentId) {
              return;
            }
            
            // Each student has an attendance array
            if (student.attendance && Array.isArray(student.attendance)) {
              student.attendance.forEach((attendanceRecord: any) => {
                try {
                  // Handle date parsing
                  let recordDate: Date;
                  
                  if (attendanceRecord.date) {
                    if (typeof attendanceRecord.date === 'string') {
                      // Parse string date (format: "2025-04-06")
                      recordDate = new Date(attendanceRecord.date);
                    } else if (attendanceRecord.date.toDate && typeof attendanceRecord.date.toDate === 'function') {
                      // Firestore Timestamp
                      recordDate = attendanceRecord.date.toDate();
                    } else if (attendanceRecord.date instanceof Date) {
                      recordDate = attendanceRecord.date;
                    } else {
                      // Try to parse as timestamp
                      recordDate = new Date(attendanceRecord.date);
                    }
                  } else {
                    console.warn('No date in attendance record:', attendanceRecord);
                    return;
                  }
                  
                  // Check if date is valid
                  if (isNaN(recordDate.getTime())) {
                    console.warn('Invalid date in attendance record:', attendanceRecord.date);
                    return;
                  }
                  
                  // Check if date is within range
                  if (recordDate >= start && recordDate <= end) {
                    // Determine status
                    let status: 'Present' | 'Absent' = 'Present';
                    if (attendanceRecord.status) {
                      status = attendanceRecord.status === 'Present' ? 'Present' : 'Absent';
                    }
                    
                    // Create attendance record
                    attendanceData.push({
                      id: `${classDoc.id}-${student.id}-${recordDate.getTime()}`,
                      studentId: student.id?.toString() || 'unknown',
                      studentName: student.name || `Student ${student.id}`,
                      rollNumber: student.id?.toString() || 'N/A',
                      classId: classDoc.id,
                      className: classData.name || classDoc.id,
                      date: recordDate,
                      status: status
                    });
                  }
                } catch (error) {
                  console.warn('Error processing attendance record:', error, attendanceRecord);
                }
              });
            }
          });
        }
      }

      console.log(`Found ${attendanceData.length} attendance records for date range`);
      
      // Sort by date for better display
      attendanceData.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      return attendanceData;
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      throw new Error('Failed to fetch attendance data');
    }
  }

  // Helper method to generate sample dates for demonstration
  static generateSampleDates(start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    const current = new Date(start);
    
    while (current <= end) {
      // Only add weekdays (Monday to Friday)
      if (current.getDay() !== 0 && current.getDay() !== 6) {
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
      
      // Limit to reasonable number of dates to avoid too much data
      if (dates.length >= 20) break;
    }
    
    return dates;
  }

  // Calculate attendance statistics
  static calculateAttendanceStats(attendanceRecords: AttendanceData[]): AttendanceStats {
    const totalSessions = attendanceRecords.length;
    const presentSessions = attendanceRecords.filter(record => record.status === 'Present').length;
    const absentSessions = totalSessions - presentSessions;
    const attendanceRate = totalSessions > 0 ? (presentSessions / totalSessions) * 100 : 0;

    return {
      totalSessions,
      presentSessions,
      absentSessions,
      attendanceRate: Math.round(attendanceRate * 100) / 100
    };
  }

  // Generate student attendance report
  static async generateStudentReport(filters: ReportFilters): Promise<StudentAttendanceReport[]> {
    const attendanceData = await this.getAttendanceData(filters);
    const studentReports: StudentAttendanceReport[] = [];
    
    // Group by student
    const studentGroups = attendanceData.reduce((groups, record) => {
      const key = `${record.studentId}-${record.classId}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(record);
      return groups;
    }, {} as Record<string, AttendanceData[]>);

    Object.entries(studentGroups).forEach(([key, records]) => {
      const firstRecord = records[0];
      const attendanceStats = this.calculateAttendanceStats(records);
      
      studentReports.push({
        studentId: firstRecord.studentId,
        studentName: firstRecord.studentName,
        rollNumber: firstRecord.rollNumber,
        attendanceStats,
        recentAttendance: records.slice(-10) // Last 10 records
      });
    });

    return studentReports.sort((a, b) => a.studentName.localeCompare(b.studentName));
  }

  // Generate class report
  static async generateClassReport(filters: ReportFilters): Promise<ClassReport[]> {
    const attendanceData = await this.getAttendanceData(filters);
    const classReports: ClassReport[] = [];
    
    // Group by class
    const classGroups = attendanceData.reduce((groups, record) => {
      if (!groups[record.classId]) {
        groups[record.classId] = [];
      }
      groups[record.classId].push(record);
      return groups;
    }, {} as Record<string, AttendanceData[]>);

    for (const [classId, records] of Object.entries(classGroups)) {
      const className = records[0]?.className || classId;
      const uniqueStudents = new Set(records.map(r => r.studentId)).size;
      const attendanceStats = this.calculateAttendanceStats(records);
      
      // Get student reports for this class
      const studentReports = await this.generateStudentReport({
        ...filters,
        classId
      });

      classReports.push({
        classId,
        className,
        totalStudents: uniqueStudents,
        attendanceStats,
        students: studentReports
      });
    }

    return classReports.sort((a, b) => a.className.localeCompare(b.className));
  }

  // Generate summary report
  static async generateSummaryReport(filters: ReportFilters): Promise<ReportSummary> {
    const attendanceData = await this.getAttendanceData(filters);
    const classReports = await this.generateClassReport(filters);
    
    const totalClasses = classReports.length;
    const totalStudents = classReports.reduce((sum, report) => sum + report.totalStudents, 0);
    const overallAttendanceRate = totalStudents > 0 
      ? this.calculateAttendanceStats(attendanceData).attendanceRate
      : 0;

    // Top performing classes
    const topPerformingClasses = classReports
      .map(report => ({
        className: report.className,
        attendanceRate: report.attendanceStats.attendanceRate
      }))
      .sort((a, b) => b.attendanceRate - a.attendanceRate)
      .slice(0, 5);

    // Low attendance students (less than 75%)
    const lowAttendanceStudents: { studentName: string; className: string; attendanceRate: number }[] = [];
    classReports.forEach(classReport => {
      classReport.students.forEach(student => {
        if (student.attendanceStats.attendanceRate < 75) {
          lowAttendanceStudents.push({
            studentName: student.studentName,
            className: classReport.className,
            attendanceRate: student.attendanceStats.attendanceRate
          });
        }
      });
    });

    // Sort by lowest attendance rate
    lowAttendanceStudents.sort((a, b) => a.attendanceRate - b.attendanceRate);

    return {
      totalClasses,
      totalStudents,
      overallAttendanceRate,
      classReports,
      topPerformingClasses,
      lowAttendanceStudents: lowAttendanceStudents.slice(0, 10)
    };
  }

  // Generate report based on type
  static async generateReport(filters: ReportFilters): Promise<any> {
    switch (filters.reportType) {
      case 'attendance':
        return await this.getAttendanceData(filters);
      case 'student':
        return await this.generateStudentReport(filters);
      case 'class':
        return await this.generateClassReport(filters);
      case 'summary':
        return await this.generateSummaryReport(filters);
      default:
        throw new Error(`Unsupported report type: ${filters.reportType}`);
    }
  }

  // Get quick stats for dashboard
  static async getQuickStats(): Promise<{
    todayAttendance: number;
    weeklyAttendance: number;
    monthlyAttendance: number;
    totalReports: number;
  }> {
    try {
      const today = await this.getAttendanceData({ 
        reportType: 'attendance', 
        dateRange: 'today' 
      });
      const thisWeek = await this.getAttendanceData({ 
        reportType: 'attendance', 
        dateRange: 'week' 
      });
      const thisMonth = await this.getAttendanceData({ 
        reportType: 'attendance', 
        dateRange: 'month' 
      });

      const todayStats = this.calculateAttendanceStats(today);
      const weeklyStats = this.calculateAttendanceStats(thisWeek);
      const monthlyStats = this.calculateAttendanceStats(thisMonth);

      return {
        todayAttendance: todayStats.attendanceRate || 0,
        weeklyAttendance: weeklyStats.attendanceRate || 0,
        monthlyAttendance: monthlyStats.attendanceRate || 0,
        totalReports: thisMonth.length || 0
      };
    } catch (error) {
      console.error('Error getting quick stats:', error);
      return {
        todayAttendance: 0,
        weeklyAttendance: 0,
        monthlyAttendance: 0,
        totalReports: 0
      };
    }
  }
} 